import RequestError from '@app/src/errors/RequestError'
import useApi from '@app/src/hooks/api'
import useErrorNotification from '@app/src/hooks/errorNotification'
import type { Facet, FacetItem, FacetParam, FacetRequest, FacetResponse } from '@app/src/pages/ResourceCollection'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'

import type { RequestParameters } from '@app/src/types/api'
import type { Sort } from '@app/src/types/filter'
import { triggerDownloadFromResponse } from '@app/src/utils/helpersTs'
import { DEFAULT_TABLE_PAGE_SIZE } from '@app/src/wf-constants'
import type { UseInfiniteQueryOptions, UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import { useInfiniteQuery, useQueries, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

export enum FetchKey {
  Account = 'Account',
  PlatformReleases = 'PlatformReleases',
  AccessibleOrganizations = 'AccessibleOrganizations',
  Organization = 'Organization',
  Country = 'Country',
  CountryFacets = 'CountryFacets',
  Supplier = 'Supplier',
  Provider = 'Provider',
  SavedViews = 'SavedViews',
  ProviderRisk = 'ProviderRisk',
  ProviderRiskFacets = 'ProviderRiskFacets',
  ProviderRiskCount = 'ProviderRiskCount',
  ProviderRiskTrend = 'ProviderRiskTrend',
  SustainabilityTopics = 'SustainabilityTopics',
  ProviderCount = 'ProviderCount',
  ProvidersByRiskRating = 'ProvidersByRiskRating',
  ProviderCollection = 'ProviderCollection',
  AllProviders = 'AllProviders',
  PendingClaimsCollection = 'PendingClaimsCollection',
  Company = 'Company',
  Investee = 'Investee',
  Request = 'Request',
  RequestItem = 'RequestItem',
  RequestStatus = 'RequestStatus',
  RequestDueAt = 'RequestDueAt',
  RequestForAllTemplates = 'RequestForAllTemplates',
  RequestItemTemplates = 'RequestItemTemplates',
  Inquiry = 'Inquiry',
  AllInquiries = 'AllInquiries',
  AllMappingRequests = 'AllMappingRequests',
  InquiryFacets = 'InquiryFacets',
  InquiryOpenRequests = 'InquiryOpenRequests',
  InquiryOverDueOpenRequests = 'InquiryOverDueOpenRequests',
  InquiryResponses = 'InquiryResponses',
  InquiryStatus = 'InquiryStatus',
  InquiryOverdue = 'InquiryOverdue',
  InquiryByProvider = 'InquiryByProvider',
  InquiryCount = 'InquiryCount',
  RequestedInquiries = 'RequestedInquiries',
  Response = 'Response',
  AllResponses = 'AllResponses',
  ResponseItemsCollection = 'ResponseItemsCollection',
  FindingsProviders = 'FindingsProviders',
  NaceCodes = 'NaceCodes',
  ResponseSharing = 'ResponseSharing',
  Answer = 'Answer',
  Subscription = 'Subscription',
  SubscriptionFacets = 'SubscriptionFacets',
  SolutionOrganizations = 'SolutionOrganizations',
  Risk = 'Risk',
  RiskMap = 'RiskMap',
  RiskScreening = 'RiskScreening',
  RequestHistory = 'RequestHistory',
  RequestPerPeriod = 'RequestPerPeriod',
  AccessorOrganization = 'AccessorOrganization',

  Product = 'Product',
  ProductCollection = 'ProductCollection',
  ProductCount = 'ProductCount',
  ProductChangedSinceSubmissionCount = 'ProductChangedSinceSubmissionCount',
  ProductFacets = 'ProductFacets',
  MappingNode = 'MappingNode',
  MappingNodeCollection = 'MappingNodeCollection',
  MappingNodeFacets = 'MappingNodeFacets',
  MappingRequests = 'MappingRequests',
  ReceivedMappingRequests = 'ReceivedMappingRequests',
  ReceivedMappingRequestChangedSinceSubmissionCount = 'ReceivedMappingRequestChangedSinceSubmissionCount',
  MappingRequestFacets = 'MappingRequestFacets',
  MappingRequestCount = 'MappingRequestCount',
  MappingRequestsByProduct = 'MappingRequestsByProduct',
  PendingMappingRequests = 'MappingRequestsPending',
  MappingResponseConnections = 'MappingResponseConnections',
  MappingResponseCollection = 'MappingResponseCollection',
  MappingResponseFacets = 'MappingResponseFacets',
  MappingResponseCount = 'MappingResponseCount',
  ActorTypeCollection = 'ActorTypeCollection',
  ActorTypeFacets = 'ActorTypeFacets',

  GoalTracker = 'GoalTracker',
  OptionsMatchGoal = 'OptionsMatchGoal',
  FileUploadMatchGoal = 'FileUploadMatchGoal',
  ProvidersMatchingFilters = 'ProvidersMatchingFilters',
  ProvidersMatchingCount = 'ProvidersMatchingCount',

  Link = 'Link',
  Notification = 'Notification',
  Contact = 'Contact',
  Colleague = 'Colleague',
  Invitation = 'Invitation',
  Claim = 'Claim',
  RequestTemplate = 'RequestTemplate',
  RequestTemplateByItemId = 'RequestTemplateByItemId',
  OrganizationOwnedTemplates = 'OrganizationOwnedTemplates',
  SharedAndOwnedTemplates = 'SharedAndOwnedTemplates',
  Questions = 'Questions',
  AuditLog = 'AuditLog',
  Network = 'Network',
  DataPoint = 'DataPoint',
  Framework = 'Framework',
  Indicator = 'Indicator',
  OrganizationFacets = 'OrganizationFacets',
  ReportFlags = 'ReportFlags',
  RequestTemplateByItemIdAndDate = 'RequestTemplateByItemIdAndDate',
  RequestTemplateOnOverTimePage = 'RequestTemplateOnOverTimePage',
  ProviderProgressPage = 'ProviderProgressPage',
  ProviderActivity = 'ProviderActivityPage',

  QuestionFacets = 'QuestionFacets',
  ProviderFacets = 'ProviderFacets',
  ProviderReferralContactFacets = 'ProviderReferralContactFacets',
  ContactFacets = 'ContactFacets',
  RequestFacets = 'RequestFacets',
  AnswerFacets = 'AnswerFacets',
  ResponseFacets = 'ResponseFacets',
  RequestTemplateFacets = 'RequestTemplateFacets',
  NotificationFacets = 'NotificationFacets',
  RiskScreeningHqRiskStatusFacet = 'RiskScreeningHqRiskStatusFacet',
  RiskScreeningHqProviderFacet = 'RiskScreeningHqProviderFacet',
  ResponseItemCustomFlags = 'ResponseItemCustomFlags',
  ResponseItemStandardFlags = 'ResponseItemStandardFlags',
  ResponseItemsFacets = 'ResponseItemFacets',
  ResponseItemCount = 'ResponseItemCount',

  ReferralCode = 'ReferralCode',
  ReferralCodeCollection = 'ReferralCodeCollection',

  FindOrganization = 'FindOrganization',
  ExistingOrganization = 'ExistingOrganization',

  Category = 'Category',
  CategoryOption = 'CategoryOption',

  Automation = 'Automation',
  AutomationFacets = 'AutomationFacets',
  AutomationAffectedCount = 'AutomationAffectedCount',
  EligibleEnrichmentCount = 'EligibleEnrichmentCount',
  EligibleScreeningCount = 'EligibleScreeningCount',

  ExportTemplate = 'ExportTemplate',
  Picker = 'Picker',
  OrganizationCollection = 'OrganizationCollection',
  Users = 'Users',
  OpenRequests = 'OpenRequests',
  OverdueRequests = 'OverdueRequests',
  RequestsOverdueWithinAWeek = 'RequestsOverdueWithinAWeek',
  RequestsOverdueWithinOverAWeek = 'RequestsOverdueWithinOverAWeek',
  OverdueProviders = 'OverdueProviders',
  SFDRRequests = 'SFDRRequests',
  SFDRPrivateTemplates = 'SFDRPrivateTemplates',
  SFDRWfTemplates = 'SFDRWfTemplates',

  Languages = 'Languages',

  QuestionnaireRequestStatistics = 'QuestionnaireRequestStatistics',
  QuestionnaireCompanyStatistics = 'QuestionnaireCompanyStatistics',
  Unit = 'Unit',

  OrganizationSettings = 'OrganizationSettings',
  DueDiligenceIntent = 'DueDiligenceIntent',
  OrganizationCsmSettingsById = 'OrganizationCsmSettingsById',
  OrganizationEntitlements = 'OrganizationEntitlements',
  EntitlementPolicies = 'EntitlementPolicies',
  ScopeEntitlements = 'ScopeEntitlements',
  ActualUserCounts = 'ActualUserCounts',
  OrganizationsExceedingContract = 'OrganizationsExceedingContract',
  ActualSupplierCounts = 'ActualSupplierCounts',
  OrganizationsExceedingSupplierContract = 'OrganizationsExceedingSupplierContract',
  GetMyOrganization = 'GetMyOrganization',

  FilterFacet = 'FilterFacet',
  FileCollection = 'FileCollection',
  CommentCollection = 'CommentCollection',

  SuggestedResponse = 'SuggestedResponse',
  FileRepository = 'FileRepository',
  SuggestedFiles = 'SuggestedFiles',
  OrganizationRiskSettings = 'OrganizationRiskSettings',

  ReferralContactCollection = 'ReferralContactCollection',
  ReferralEmailHistoryCollection = 'ReferralEmailHistoryCollection',
  ReferralContactExists = 'ReferralContactExists',

  OrganizationAssessmentSettings = 'OrganizationAssessmentSettings',
  OrganizationAssessmentSettingsFacets = 'OrganizationAssessmentSettingsFacets',
  AssessmentCollection = 'AssessmentCollection',
  AssessmentQuestionnaireLink = 'AssessmentQuestionnaireLink',
  AssessmentFacets = 'AssessmentFacets',
  AssessmentCount = 'AssessmentCount',
  AssessmentTemplateCollection = 'AssessmentTemplateCollection',
  AssessmentTemplateWithClassificationRules = 'AssessmentTemplateWithClassificationRules',
  AssessmentTemplateQuestionnaireSyncStatus = 'AssessmentTemplateQuestionnaireSyncStatus',
  AssessmentTemplateRiskIndices = 'AssessmentTemplateRiskIndices',
  AssessmentTemplateRiskIndicesCollection = 'AssessmentTemplateRiskIndicesCollection',

  SuggestedContacts = 'SuggestedContacts',
  ResponseItemDataInsights = 'ResponseItemDataInsights',
  AssessmentTemplates = 'AssessmentTemplates',
  AccessorFeature = 'AccessorFeature',
  JobRunning = 'JobRunning',
  ExcelExportSettings = 'ExcelExportSettings',
  ResponseScraping = 'ResponseScraping',
  ResponseScrapingFacets = 'ResponseScrapingFacets',
  ResponseScrapingAdminFilter = 'ResponseScrapingAdminFilter',
  ResponseScrapingAdminFacets = 'ResponseScrapingAdminFacets',
  ResponseScrapingCount = 'ResponseScrapingCount',
  QuestionCategories = 'QuestionCategories',
  ExactMatchExists = 'ExactMatchExists',
  BulkFindOrganizations = 'BulkFindOrganizations',
  BulkFindExactMatches = 'BulkFindExactMatches',
  PreviousAnswers = 'PreviousAnswers',
  ScrapedAnswer = 'ScrapedAnswer',
  DefaultSystemPrompt = 'DefaultSystemPrompt',
  RiskIndexType = 'RiskIndexType',
  InsightsReport = 'InsightsReport',
  InsightsReportItem = 'InsightsReportItem',
  IntercomUserHash = 'IntercomUserHash',
  DueDiligenceReport = 'DueDiligenceReport',
  AnswerFlagRuleCollection = 'AnswerFlagRuleCollection',
  ExpectationAutomation = 'ExpectationAutomation',
  ExpectationAffectedCount = 'ExpectationAffectedCount',
  ExpectationCollection = 'ExpectationCollection',
  Expectation = 'Expectation',
  TabularImportStatus = 'TabularImportStatus',
  TabularImportNewProviderIds = 'TabularImportNewProviderIds',

  AssessmentBenchmarkCollection = 'AssessmentBenchmarkCollection',
  AssessmentBenchmarkFacets = 'AssessmentBenchmarkFacets',
  FindingsBenchmarkCollection = 'FindingsBenchmarkCollection',
  FindingsBenchmarkFacets = 'FindingsBenchmarkFacets',
  SustainabilityProfileBenchmark = 'SustainabilityProfileBenchmark',
}

export type FetchBaseParameters<T> = {
  endpoint: string
  key: FetchKey | [FetchKey, ...Array<FetchKey | string | number | undefined>]
  options?: Omit<UseQueryOptions<T, RequestError>, 'queryKey' | 'queryFn'>
  config?: RequestParameters['config']
  showSnackbarOn404?: boolean
  shouldShowErrorNotification?: boolean
}

export interface FetchResourceWithBodyParameters<TReturn, TPayload> extends FetchBaseParameters<TReturn> {
  body: TPayload
}

export type FetchBaseInfiniteParameters<T> = {
  options?: Omit<
    UseInfiniteQueryOptions<T, RequestError>,
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
  >
} & FetchBaseParameters<T>

export interface FetchFacetsOptions extends FetchBaseParameters<FacetResponse> {
  filter?: FilterGroup[]
  facetsParam?: Array<FacetParam>
  skipNotSet?: boolean
  sort?: Sort
}

export interface FetchCollectionParameters<T> extends FetchBaseParameters<CollectionResponseType<T>> {
  payload: {
    filter: FilterGroup[]
    include: string[]
    sort?: Sort
    pagination?: { pageNumber: number; itemsPerPage: number }
    export?: {}
  }
}

export interface FetchParameters<TReturn, TPayload> extends FetchBaseParameters<TReturn> {
  payload: TPayload
}

export interface FetchInfiniteCollectionParameters<T> extends FetchBaseInfiniteParameters<CollectionResponseType<T>> {
  payload: {
    filter: FilterGroup[]
    include: string[]
    sort?: Sort
    pagination?: { pageNumber: number; itemsPerPage: number }
    export?: {}
  }
}

//--------FETCH RESOURCE COLLECTION--------//
export interface CollectionResponseType<T> {
  items: T[]
  totalResult: number
  itemsPerPage: number
  pageNumber: number
  pagesCount: number
}

export type UseFetchCollectionResultType<T, RequestError> = UseQueryResult<CollectionResponseType<T>, RequestError> & {
  items: T[]
  count: number
  pageNumber?: number
  pagesCount?: number
}

export const useFetchCollectionWithPost = <T>({
  key,
  endpoint,
  payload,
  config,
  options,
  shouldShowErrorNotification,
}: FetchCollectionParameters<T>): UseFetchCollectionResultType<T, RequestError> => {
  const { showErrorNotification } = useErrorNotification()
  const { post } = useApi()

  const queryResult = useQuery<CollectionResponseType<T>, RequestError>({
    queryKey: [key, payload].flat(),
    queryFn: () =>
      post<CollectionResponseType<T>, FetchCollectionParameters<T>['payload']>({
        url: endpoint,
        body: payload,
        config,
      }),
    ...options,
  })

  useEffect(() => {
    if (queryResult.isError) {
      if (shouldShowErrorNotification ?? true) showErrorNotification({ requestError: queryResult.error })

      if (queryResult.error.statusCode === 401) {
        throw queryResult.error
      }
    }
  }, [queryResult.isError])

  const { items = [], totalResult: count = 0, pageNumber, pagesCount } = queryResult.data ?? {}

  return { ...queryResult, items, count, pageNumber, pagesCount }
}

export const useInfiniteFetchCollection = <T>({
  key,
  endpoint,
  payload,
  config,
  options,
  shouldShowErrorNotification,
}: FetchInfiniteCollectionParameters<T>) => {
  const { showErrorNotification } = useErrorNotification()
  const { post } = useApi()

  const { select, ...safeOptions } = options || {}
  const queryResult = useInfiniteQuery<CollectionResponseType<T>, RequestError>({
    queryKey: Array.isArray(key) ? key : [key],
    queryFn: ({ pageParam = 1 }) =>
      post<CollectionResponseType<T>, FetchCollectionParameters<T>['payload']>({
        url: endpoint,
        body: {
          ...payload,
          pagination: {
            itemsPerPage: payload.pagination?.itemsPerPage ?? DEFAULT_TABLE_PAGE_SIZE,
            pageNumber: Number(pageParam) ?? 1,
          },
        },
        config,
      }),
    initialPageParam: 1,
    getNextPageParam: lastPage => (lastPage.pageNumber < lastPage.pagesCount ? lastPage.pageNumber + 1 : undefined),
    ...safeOptions,
  })

  useEffect(() => {
    if (queryResult.isError) {
      if (shouldShowErrorNotification ?? true) showErrorNotification({ requestError: queryResult.error })

      if (queryResult.error.statusCode === 401) {
        throw queryResult.error
      }
    }
  }, [queryResult.isError])

  return queryResult
}

export const useFetchResourceWithPost = <TReturn, TPayload = Array<number | string>>({
  endpoint,
  key,
  options,
  body,
  showSnackbarOn404 = true,
  shouldShowErrorNotification,
}: FetchResourceWithBodyParameters<TReturn, TPayload>): UseQueryResult<TReturn, RequestError> => {
  const { post } = useApi()
  const { showErrorNotification } = useErrorNotification()

  const queryResult = useQuery<TReturn, RequestError>({
    queryKey: [key, body].flat(),
    queryFn: async () => await post({ url: endpoint, body }),
    ...options,
  })

  useEffect(() => {
    const is404 = queryResult.isError && queryResult.error.statusCode === 404

    if ((queryResult.isError && !is404) || (is404 && showSnackbarOn404)) {
      if (shouldShowErrorNotification ?? true) showErrorNotification({ requestError: queryResult.error })
    }

    if (queryResult?.error?.statusCode === 401) {
      throw queryResult.error
    }
  }, [queryResult.isError])

  return queryResult
}

export const useFetchResource = <T>({
  endpoint,
  key,
  options,
  showSnackbarOn404 = true,
  shouldShowErrorNotification,
}: FetchBaseParameters<T>): UseQueryResult<T, RequestError> => {
  const { get } = useApi()
  const { showErrorNotification } = useErrorNotification()

  const queryResult = useQuery<T, RequestError>({
    queryKey: Array.isArray(key) ? key : [key],
    queryFn: async () => {
      const result = await get<T>({ url: endpoint })
      // React Query v5 requires queryFn to return a non-undefined value
      // Return null instead of undefined when API returns no content (204)
      // We cast to T because the hook consumers expect T, and they handle null/undefined checks
      return (result === undefined ? null : result) as T
    },
    ...options,
  })

  // TODO Move somewhere else?
  useEffect(() => {
    const is404 = queryResult.isError && queryResult.error.statusCode === 404

    if ((queryResult.isError && !is404) || (is404 && showSnackbarOn404)) {
      if (shouldShowErrorNotification ?? true) showErrorNotification({ requestError: queryResult.error })
    }

    if (queryResult?.error?.statusCode === 401) {
      throw queryResult.error
    }
  }, [queryResult.isError])

  return queryResult
}
//-------------------------------------//

//--------FETCH FACETS--------//

type UseFetchFacet = Omit<UseQueryResult, 'data' | 'error'> & {
  items: Facet[]
  facets: FacetItem[][]
  count: number
  isPending: boolean
  isFetching: boolean
  error: RequestError | null
  isError: boolean
}

export const useFetchFacets = ({
  endpoint,
  filter = [],
  facetsParam = [],
  sort,
  key,
  options,
  skipNotSet = false,
}: FetchFacetsOptions): UseFetchFacet => {
  const { post } = useApi()
  const queryResult = useQuery<FacetResponse, RequestError>({
    queryKey: [key, filter, facetsParam, sort].flat(),
    queryFn: async () => {
      return await post<FacetResponse, FacetRequest>({
        url: endpoint,
        body: {
          filter,
          facets: facetsParam,
          sort,
        },
      })
    },
    ...options,
  })

  return handleFacetQueryResult(queryResult, skipNotSet, facetsParam)
}

export const useFetchFacetsQueries = (fetchFacetsOptionsArray: FetchFacetsOptions[]): UseFetchFacet[] => {
  const { post } = useApi()

  // Mapping the results inside `combine` rather than after the hook lets React Query run the mapped array
  // through its structural comparison, so callers keep the same reference while the facet data is unchanged.
  // Mapping outside returned a new array of new objects on every render, which invalidated every downstream
  // useMemo — for the MRT tables that meant rebuilding every column on every render.
  return useQueries({
    queries: fetchFacetsOptionsArray.map(({ endpoint, filter, facetsParam, sort, key, options }) => ({
      queryKey: [key, filter, facetsParam, sort].flat(),
      queryFn: async () => {
        return await post<FacetResponse, FacetRequest>({
          url: endpoint,
          body: {
            filter,
            facets: facetsParam,
            sort,
          },
        })
      },
      ...options,
    })),
    combine: results =>
      results.map((queryResult, index) =>
        handleFacetQueryResult(
          queryResult,
          fetchFacetsOptionsArray[index].skipNotSet,
          fetchFacetsOptionsArray[index].facetsParam,
        ),
      ),
  })
}

const handleFacetQueryResult = (
  { data, isPending, error, isFetching, isError, ...rest }: UseQueryResult<FacetResponse, RequestError>,
  skipNotSet: boolean | undefined,
  facetsParam: FacetParam[] | undefined,
) => {
  let items = data?.facets || []
  if (skipNotSet) {
    items = items.map(facet => {
      facet.items = facet.items.filter(item => item.label !== 'NotSet')
      return facet
    })
  }
  const count = data?.total || 0
  const facets = facetsParam?.map((_, i) => items?.[i]?.items) // here we're assuming the backend will send facets back in the same order as they were requested. If we have bugs we need a fancier method to select the correct facet
  return { items, facets: facets || [], count, isPending, isFetching, error: error as RequestError, isError, ...rest }
}

//-------------------------------------//

export const useFetchPost = <TReturn, TPayload>({
  key,
  endpoint,
  payload,
  config,
  options,
  shouldShowErrorNotification,
}: FetchParameters<TReturn, TPayload>): UseQueryResult<TReturn, RequestError> => {
  const { showErrorNotification } = useErrorNotification()
  const { post } = useApi()

  const queryResult = useQuery<TReturn, RequestError>({
    queryKey: [key, payload].flat(),
    queryFn: () =>
      post<TReturn, TPayload>({
        url: endpoint,
        body: payload,
        config,
      }),
    ...options,
  })

  useEffect(() => {
    if (queryResult.isError) {
      if (shouldShowErrorNotification ?? true) showErrorNotification({ requestError: queryResult.error })

      if (queryResult.error.statusCode === 401) {
        throw queryResult.error
      }
    }
  }, [queryResult.isError])

  return queryResult
}

export const useFetchCount = (fetchParameters: FetchParameters<number, FilterGroup[]>) => {
  const results = useFetchPost<number, FilterGroup[]>(fetchParameters)
  const { data, ...mappedResults } = { ...results, count: results.data }
  return mappedResults
}

/**
 * Several counts at once, under the same keys as useFetchCount so a count already fetched is reused. Failures
 * are handled as useFetchPost handles them, once for the batch rather than once per count.
 */
export const useFetchCounts = (
  fetchParametersArray: FetchParameters<number, FilterGroup[]>[],
  { shouldShowErrorNotification = true }: { shouldShowErrorNotification?: boolean } = {},
): { counts: (number | undefined)[]; isLoading: boolean; isError: boolean } => {
  const { showErrorNotification } = useErrorNotification()
  const { post } = useApi()
  const { counts, isLoading, error } = useQueries({
    queries: fetchParametersArray.map(({ key, endpoint, payload, config, options }) => ({
      queryKey: [key, payload].flat(),
      queryFn: () => post<number, FilterGroup[]>({ url: endpoint, body: payload, config }),
      ...options,
    })),
    combine: results => ({
      counts: results.map(result => result.data),
      isLoading: results.some(result => result.isLoading),
      error: results.find(result => result.isError)?.error as RequestError | undefined,
    }),
  })

  useEffect(() => {
    if (!error) return
    if (shouldShowErrorNotification) showErrorNotification({ requestError: error })
    if (error.statusCode === 401) throw error
  }, [error])

  return { counts, isLoading, isError: Boolean(error) }
}

export const useFileDownload = () => {
  const { get, post } = useApi()
  const { showErrorNotification } = useErrorNotification()
  const [isFileDownloading, setIsFileDownloading] = useState(false)

  const handleFileDownload = async (endpoint: string, fileExtension?: string) => {
    setIsFileDownloading(true)

    const headers = new Headers()
    headers.append('Content-Type', 'application/octet-stream')

    try {
      const { blob, fileName } = await get<{ blob: Blob; fileName: string }>(
        {
          url: endpoint,
          config: { headers },
        },
        { isBlob: true },
      )
      triggerDownloadFromResponse(blob, fileName, fileExtension)
    } catch (e) {
      if (e instanceof RequestError) {
        showErrorNotification({ requestError: e })
      }
    } finally {
      setIsFileDownloading(false)
    }
  }
  const handleFileDownloadWithFilters = async <T>(
    endpoint: string,
    payload: FetchCollectionParameters<T>['payload'],
    fileExtension?: string,
  ) => {
    setIsFileDownloading(true)

    const headers = new Headers()
    headers.append('Content-Type', 'application/octet-stream')

    try {
      const queryResult = await post<{ blob: Blob; fileName: string }, FetchCollectionParameters<T>['payload']>(
        {
          body: payload,
          url: endpoint,
          config: { headers },
        },
        { isBlob: true },
      )
      triggerDownloadFromResponse(queryResult.blob, queryResult.fileName, fileExtension)
    } catch (e) {
      if (e instanceof RequestError) {
        showErrorNotification({ requestError: e })
      }
    } finally {
      setIsFileDownloading(false)
    }
  }

  return {
    downloadFile: handleFileDownload,
    isFileDownloading,
    handleFileDownloadWithFilters: handleFileDownloadWithFilters,
  }
}
